import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { BigDisplay } from '../types/BigDisplay';
import { getBigDisplayData } from '../services/bigDisplayData';

interface DisplaysContextType {
  displays: BigDisplay[];
  loading: boolean;
  error: string | null;
  refreshDisplays: () => Promise<void>;
  updateDisplay: (displayId: number, updates: Partial<BigDisplay>) => void;
}

const DisplaysContext = createContext<DisplaysContextType | undefined>(undefined);

interface DisplaysProviderProps {
  children: ReactNode;
}

export const DisplaysProvider: React.FC<DisplaysProviderProps> = ({ children }) => {
  const [displays, setDisplays] = useState<BigDisplay[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);

  const fetchDisplays = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const storeId = localStorage.getItem('storeId');
      
      if (!storeId) {
        // console.log('Store ID not found, retry count:', retryCount);
        
        // Retry up to 3 times with increasing delays
        if (retryCount < 3) {
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            fetchDisplays();
          }, 500 * (retryCount + 1)); // 500ms, 1s, 1.5s delays
          return;
        }
        
        setError('Store ID not found in localStorage');
        setLoading(false);
        return;
      }
      
      // Reset retry count when storeId is found
      setRetryCount(0);
      
      console.log('Fetching displays for storeId:', storeId); // Debug log
      
      const response = await getBigDisplayData(storeId, 100, 0);
      
      setDisplays(response.displays || []);
      
    } catch (err) {
      console.error('Error fetching displays:', err);
      setError('Failed to fetch displays. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refreshDisplays = async () => {
    setRetryCount(0); // Reset retry count when manually refreshing
    await fetchDisplays();
  };

  const updateDisplay = (displayId: number, updates: Partial<BigDisplay>) => {
    setDisplays(prevDisplays => 
      prevDisplays.map(display => 
        display.id === displayId 
          ? { ...display, ...updates }
          : display
      )
    );
  };

  useEffect(() => {
    fetchDisplays();
    
    // Listen for storage events (when localStorage changes)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'storeId' && e.newValue) {
        console.log('StoreId changed in localStorage, refetching displays');
        setRetryCount(0);
        fetchDisplays();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const contextValue: DisplaysContextType = {
    displays,
    loading,
    error,
    refreshDisplays,
    updateDisplay,
  };

  return (
    <DisplaysContext.Provider value={contextValue}>
      {children}
    </DisplaysContext.Provider>
  );
};

export const useDisplays = (): DisplaysContextType => {
  const context = useContext(DisplaysContext);
  if (!context) {
    throw new Error('useDisplays must be used within a DisplaysProvider');
  }
  return context;
}; 